import { useTranslation } from 'react-i18next';
import type { UseFormReturn, FieldErrors } from 'react-hook-form';
import type { ReportDefinitionFormValues } from '../schema/reportBuilder.schema';

interface ColumnBuilderTableProps {
  form: UseFormReturn<ReportDefinitionFormValues>;
  onAdd: () => void;
}

const ALIGN_OPTIONS = ['LEFT', 'CENTER', 'RIGHT'] as const;
const BARCODE_OPTIONS = [null, 'QR_CODE', 'CODE_128'] as const;
const EC_OPTIONS = ['L', 'M', 'Q', 'H'] as const;

function fieldError(errors: FieldErrors<ReportDefinitionFormValues>, index: number, field: string): string | undefined {
  const cols = errors.columns;
  if (!cols || !Array.isArray(cols)) return undefined;
  const colErr = cols[index] as Record<string, { message?: string }> | undefined;
  if (!colErr) return undefined;
  return colErr[field]?.message;
}

/**
 * ColumnBuilderTable — 컬럼 구성 표 (드래그 정렬 대신 order 번호 편집)
 * react-hook-form useFieldArray 로 연결
 */
export function ColumnBuilderTable({ form, onAdd }: ColumnBuilderTableProps) {
  const { t } = useTranslation();
  const { register, watch, setValue, formState: { errors } } = form;
  const columns = watch('columns');

  function handleRemove(index: number) {
    const next = columns.filter((_, i) => i !== index).map((c, i) => ({ ...c, order: i }));
    setValue('columns', next, { shouldValidate: true });
  }

  function handleMoveUp(index: number) {
    if (index === 0) return;
    const next = [...columns];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    setValue('columns', next.map((c, i) => ({ ...c, order: i })), { shouldValidate: true });
  }

  function handleMoveDown(index: number) {
    if (index === columns.length - 1) return;
    const next = [...columns];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    setValue('columns', next.map((c, i) => ({ ...c, order: i })), { shouldValidate: true });
  }

  if (columns.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--spacing-6)',
          textAlign: 'center',
          border: '1px dashed var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-text-muted)',
          fontSize: 'var(--font-size-sm)',
        }}
      >
        {t('reportBuilder.editor.columns.empty')}
        <div style={{ marginTop: 'var(--spacing-3)' }}>
          <button
            type="button"
            onClick={onAdd}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('reportBuilder.editor.columns.add')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 'var(--font-size-sm)',
        }}
        aria-label={t('reportBuilder.editor.columns.title')}
      >
        <thead>
          <tr style={{ background: 'var(--color-surface-muted)' }}>
            {[
              t('reportBuilder.editor.columns.order'),
              t('reportBuilder.editor.columns.header'),
              t('reportBuilder.editor.columns.fieldName'),
              t('reportBuilder.editor.columns.width'),
              t('reportBuilder.editor.columns.align'),
              t('reportBuilder.editor.columns.barcode'),
              '',
            ].map((h, i) => (
              <th
                key={i}
                style={{
                  textAlign: 'left',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  borderBottom: '1px solid var(--color-surface-border)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {columns.map((col, index) => {
            const headerErr = fieldError(errors, index, 'header');
            const fieldErr = fieldError(errors, index, 'field');
            const showBarcodeSubRow = col.barcodeMode && col.barcodeMode !== null;
            return (
              <>
                <tr
                  key={`col-${index}`}
                  style={{ background: index % 2 === 0 ? 'transparent' : 'var(--color-surface-elevated)' }}
                >
                  {/* 순서 / 위아래 이동 */}
                  <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', borderBottom: '1px solid var(--color-surface-border)', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginRight: 'var(--spacing-1)' }}>{index}</span>
                    <button
                      type="button"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      aria-label="위로"
                      style={{ background: 'none', border: 'none', cursor: index === 0 ? 'not-allowed' : 'pointer', opacity: index === 0 ? 0.3 : 1, padding: '0 2px', fontSize: 'var(--font-size-xs)' }}
                    >↑</button>
                    <button
                      type="button"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === columns.length - 1}
                      aria-label="아래로"
                      style={{ background: 'none', border: 'none', cursor: index === columns.length - 1 ? 'not-allowed' : 'pointer', opacity: index === columns.length - 1 ? 0.3 : 1, padding: '0 2px', fontSize: 'var(--font-size-xs)' }}
                    >↓</button>
                  </td>

                  {/* 헤더 */}
                  <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', borderBottom: '1px solid var(--color-surface-border)' }}>
                    <input
                      {...register(`columns.${index}.header`)}
                      type="text"
                      placeholder={t('reportBuilder.editor.columns.header')}
                      aria-invalid={!!headerErr}
                      aria-describedby={headerErr ? `col-header-err-${index}` : undefined}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: 'var(--spacing-1) var(--spacing-2)',
                        border: `1px solid ${headerErr ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-sm)',
                        background: 'var(--color-surface-elevated)',
                        color: 'var(--color-text-primary)',
                        fontFamily: 'var(--font-family-sans)',
                        minWidth: 80,
                      }}
                    />
                    {headerErr && (
                      <p id={`col-header-err-${index}`} role="alert" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', margin: '2px 0 0' }}>{headerErr}</p>
                    )}
                  </td>

                  {/* 필드 */}
                  <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', borderBottom: '1px solid var(--color-surface-border)' }}>
                    <input
                      {...register(`columns.${index}.field`)}
                      type="text"
                      placeholder={t('reportBuilder.editor.columns.fieldName')}
                      aria-invalid={!!fieldErr}
                      aria-describedby={fieldErr ? `col-field-err-${index}` : undefined}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: 'var(--spacing-1) var(--spacing-2)',
                        border: `1px solid ${fieldErr ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-sm)',
                        background: 'var(--color-surface-elevated)',
                        color: 'var(--color-text-primary)',
                        fontFamily: 'var(--font-family-mono)',
                        minWidth: 80,
                      }}
                    />
                    {fieldErr && (
                      <p id={`col-field-err-${index}`} role="alert" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', margin: '2px 0 0' }}>{fieldErr}</p>
                    )}
                  </td>

                  {/* 너비 */}
                  <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', borderBottom: '1px solid var(--color-surface-border)' }}>
                    <input
                      {...register(`columns.${index}.widthRatio`, { valueAsNumber: true })}
                      type="number"
                      step="0.1"
                      min="0.1"
                      max="10"
                      style={{
                        width: 60,
                        padding: 'var(--spacing-1) var(--spacing-2)',
                        border: '1px solid var(--color-surface-border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-sm)',
                        background: 'var(--color-surface-elevated)',
                        color: 'var(--color-text-primary)',
                        fontFamily: 'var(--font-family-sans)',
                      }}
                    />
                  </td>

                  {/* 정렬 */}
                  <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', borderBottom: '1px solid var(--color-surface-border)' }}>
                    <select
                      {...register(`columns.${index}.align`)}
                      style={{
                        padding: 'var(--spacing-1) var(--spacing-2)',
                        border: '1px solid var(--color-surface-border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-sm)',
                        background: 'var(--color-surface-elevated)',
                        color: 'var(--color-text-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      {ALIGN_OPTIONS.map((a) => (
                        <option key={a} value={a}>{t(`reportBuilder.align.${a}`)}</option>
                      ))}
                    </select>
                  </td>

                  {/* 바코드 모드 */}
                  <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', borderBottom: showBarcodeSubRow ? 'none' : '1px solid var(--color-surface-border)' }}>
                    <select
                      value={col.barcodeMode ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setValue(
                          `columns.${index}.barcodeMode`,
                          val === '' ? null : (val as 'QR_CODE' | 'CODE_128'),
                          { shouldValidate: true }
                        );
                      }}
                      style={{
                        padding: 'var(--spacing-1) var(--spacing-2)',
                        border: '1px solid var(--color-surface-border)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-sm)',
                        background: 'var(--color-surface-elevated)',
                        color: 'var(--color-text-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      {BARCODE_OPTIONS.map((b) => (
                        <option key={b ?? 'none'} value={b ?? ''}>
                          {b === null ? t('reportBuilder.barcode.none') : t(`reportBuilder.barcode.${b}`)}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* 삭제 */}
                  <td style={{ padding: 'var(--spacing-2) var(--spacing-3)', borderBottom: showBarcodeSubRow ? 'none' : '1px solid var(--color-surface-border)', whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      aria-label={`${t('reportBuilder.editor.columns.delete')} ${col.header || index}`}
                      style={{
                        padding: 'var(--spacing-1) var(--spacing-2)',
                        background: 'transparent',
                        border: '1px solid var(--color-danger)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--color-danger)',
                        fontSize: 'var(--font-size-xs)',
                        cursor: 'pointer',
                      }}
                    >
                      {t('reportBuilder.editor.columns.delete')}
                    </button>
                  </td>
                </tr>

                {/* 바코드 서브행 */}
                {showBarcodeSubRow && (
                  <tr
                    key={`col-barcode-${index}`}
                    style={{ background: index % 2 === 0 ? 'var(--color-surface-elevated)' : 'transparent' }}
                  >
                    <td colSpan={2} style={{ borderBottom: '1px solid var(--color-surface-border)' }} />
                    <td colSpan={5} style={{ padding: 'var(--spacing-2) var(--spacing-3)', borderBottom: '1px solid var(--color-surface-border)' }}>
                      <div style={{ display: 'flex', gap: 'var(--spacing-4)', alignItems: 'center', flexWrap: 'wrap' }}>
                        <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
                          {t('reportBuilder.editor.columns.barcodeSource')}
                          <input
                            {...register(`columns.${index}.barcodeSourceField`)}
                            type="text"
                            placeholder="sourceField"
                            style={{
                              padding: 'var(--spacing-1) var(--spacing-2)',
                              border: '1px solid var(--color-surface-border)',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: 'var(--font-size-sm)',
                              background: 'var(--color-surface-background)',
                              color: 'var(--color-text-primary)',
                              fontFamily: 'var(--font-family-mono)',
                              minWidth: 100,
                            }}
                          />
                        </label>
                        {col.barcodeMode === 'QR_CODE' && (
                          <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
                            {t('reportBuilder.editor.columns.barcodeEc')}
                            <select
                              {...register(`columns.${index}.barcodeEc`)}
                              style={{
                                padding: 'var(--spacing-1) var(--spacing-2)',
                                border: '1px solid var(--color-surface-border)',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 'var(--font-size-sm)',
                                background: 'var(--color-surface-background)',
                                color: 'var(--color-text-primary)',
                                cursor: 'pointer',
                              }}
                            >
                              {EC_OPTIONS.map((ec) => (
                                <option key={ec} value={ec}>{ec}</option>
                              ))}
                            </select>
                          </label>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
